import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Divider, Drawer, IconButton, Skeleton, Tab, Tabs, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SyncIcon from "@mui/icons-material/Sync";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import OceanShipmentMap from "../OceanShipmentMap";
import JourneyBar from "./JourneyBar";
import {
    CARRIER_COLORS,
    CARRIER_SHORT,
    STANDARD_EVENTS,
    STANDARD_LABEL,
    TONE,
    arrivalHint,
    formatDate,
    portText,
    rowState,
    timeAgo,
} from "./constants";

export const Pill = ({ tone = "slate", children }) => (
    <MDBox
        component="span"
        sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1.1,
            py: 0.25,
            borderRadius: "999px",
            fontSize: 11.5,
            fontWeight: 600,
            color: TONE[tone].color,
            backgroundColor: TONE[tone].bg,
            border: `1px solid ${TONE[tone].border}`,
            whiteSpace: "nowrap",
        }}
    >
        {children}
    </MDBox>
);

export const CarrierBadge = ({ code, name }) => (
    <MDBox component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
        <MDBox
            component="span"
            sx={{ width: 8, height: 8, borderRadius: "2px", background: CARRIER_COLORS[code] || "#94a3b8", flexShrink: 0 }}
        />
        <MDBox component="span" sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
            {CARRIER_SHORT[code] || name || code}
        </MDBox>
    </MDBox>
);

const Field = ({ label, value }) => (
    <MDBox>
        <MDTypography variant="caption" color="text" display="block" sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>
            {label}
        </MDTypography>
        <MDTypography
            variant="button"
            fontWeight="medium"
            display="block"
            sx={{ color: "#1e293b", wordBreak: "break-word", whiteSpace: "normal", lineHeight: 1.35 }}
        >
            {value || "-"}
        </MDTypography>
    </MDBox>
);

// Latest actual (else newest estimate) event per Filentech milestone.
const milestoneEvents = (events) => {
    const map = new Map();
    for (const e of events || []) {
        if (!e.standardEvent) continue;
        const current = map.get(e.standardEvent);
        if (!current || (e.isActual && !current.isActual) || (e.isActual === current.isActual && e.eventDateTime > current.eventDateTime)) {
            map.set(e.standardEvent, e);
        }
    }
    return map;
};

function Timeline({ events }) {
    const byMilestone = useMemo(() => milestoneEvents(events), [events]);
    const items = STANDARD_EVENTS.filter(([code]) => byMilestone.has(code));

    if (!items.length) {
        return (
            <MDTypography variant="caption" color="text">
                No milestones reported yet.
            </MDTypography>
        );
    }

    return (
        <MDBox>
            {items.map(([code, label], index) => {
                const e = byMilestone.get(code);
                const actual = e.isActual;
                const last = index === items.length - 1;
                return (
                    <MDBox key={code} display="flex" gap={1.5}>
                        <MDBox display="flex" flexDirection="column" alignItems="center" sx={{ width: 14 }}>
                            <MDBox
                                sx={{
                                    width: 12,
                                    height: 12,
                                    mt: "4px",
                                    borderRadius: "50%",
                                    background: actual ? "#16a34a" : "#fff",
                                    border: `2px solid ${actual ? "#16a34a" : "#94a3b8"}`,
                                    flexShrink: 0,
                                }}
                            />
                            {!last && <MDBox sx={{ width: 2, flex: 1, minHeight: 22, background: actual ? "#bbf7d0" : "#e2e8f0" }} />}
                        </MDBox>
                        <MDBox pb={1.5} flex={1} display="flex" justifyContent="space-between" gap={2}>
                            <MDBox>
                                <MDTypography variant="button" fontWeight="bold" sx={{ color: actual ? "#0f172a" : "#64748b" }}>
                                    {label}
                                </MDTypography>
                                <MDTypography variant="caption" color="text" display="block">
                                    {[e.location?.name || e.location?.locode, e.vesselName && `${e.vesselName}${e.voyageNumber ? ` / ${e.voyageNumber}` : ""}`]
                                        .filter(Boolean)
                                        .join(" · ")}
                                </MDTypography>
                            </MDBox>
                            <MDBox textAlign="right" sx={{ whiteSpace: "nowrap" }}>
                                <MDTypography variant="caption" fontWeight="medium" display="block" sx={{ color: actual ? "#0f172a" : "#64748b" }}>
                                    {formatDate(e.eventDateTime, true)}
                                </MDTypography>
                                <MDTypography variant="caption" sx={{ color: actual ? "#15803d" : "#94a3b8", fontSize: 11 }}>
                                    {actual ? "Actual" : e.eventClassifier === "PLN" ? "Planned" : "Estimated"}
                                </MDTypography>
                            </MDBox>
                        </MDBox>
                    </MDBox>
                );
            })}
        </MDBox>
    );
}

function EventsTable({ events }) {
    if (!events?.length) {
        return (
            <MDTypography variant="caption" color="text">
                No events received yet.
            </MDTypography>
        );
    }
    return (
        <MDBox sx={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                <thead>
                    <tr style={{ textAlign: "left", color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>
                        {["Date", "Milestone", "Carrier code", "Location", "Vessel"].map((h) => (
                            <th key={h} style={{ padding: "8px", borderBottom: "1px solid #e5e7eb", fontWeight: 600 }}>
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {events.map((e) => (
                        <tr key={e._id} style={{ opacity: e.isActual ? 1 : 0.6, borderBottom: "1px solid #f1f5f9" }}>
                            <td style={{ padding: "8px", whiteSpace: "nowrap" }}>{formatDate(e.eventDateTime, true)}</td>
                            <td style={{ padding: "8px", fontWeight: 600 }}>{e.standardEvent ? STANDARD_LABEL[e.standardEvent] : e.description}</td>
                            <td style={{ padding: "8px", whiteSpace: "nowrap", color: "#475569" }}>
                                {[e.carrierEventCode, e.eventClassifier, e.emptyIndicator].filter(Boolean).join(" · ")}
                            </td>
                            <td style={{ padding: "8px" }}>{[e.location?.locode, e.location?.name].filter(Boolean).join(" – ")}</td>
                            <td style={{ padding: "8px" }}>{[e.vesselName, e.voyageNumber].filter(Boolean).join(" / ")}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </MDBox>
    );
}

function TrackingDrawer({ open, detail, loading, refreshing, onClose, onRefresh, onToggleActive, onDelete }) {
    const navigate = useNavigate();
    const [tab, setTab] = useState("overview");
    const state = detail ? rowState(detail) : null;
    const hint = detail ? arrivalHint(detail) : null;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: { xs: "100%", md: 760 }, maxWidth: "100%", m: 0, borderRadius: 0, height: "100%" } }}
        >
            {!detail || (loading && !detail.events) ? (
                <MDBox p={3}>
                    <Skeleton variant="text" width="40%" height={40} />
                    <Skeleton variant="rounded" height={120} sx={{ my: 2 }} />
                    <Skeleton variant="rounded" height={320} />
                </MDBox>
            ) : (
                <MDBox display="flex" flexDirection="column" height="100%">
                    {/* Header */}
                    <MDBox px={3} pt={2.5} pb={2} sx={{ borderBottom: "1px solid #e5e7eb", background: "linear-gradient(180deg,#f8fbff 0%,#fff 100%)" }}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                            <MDBox>
                                <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                                    <MDTypography variant="h5" fontWeight="bold" sx={{ letterSpacing: 0.3 }}>
                                        {detail.containerNumber || detail.mblNumber || detail.bookingNumber}
                                    </MDTypography>
                                    <Pill tone={state.tone}>{state.label}</Pill>
                                    {detail.isDemo && <Pill tone="slate">Demo data</Pill>}
                                </MDBox>
                                <MDBox display="flex" alignItems="center" gap={1.5} mt={0.5} flexWrap="wrap">
                                    <CarrierBadge code={detail.carrier} name={detail.carrierName} />
                                    {detail.customer && (
                                        <MDTypography variant="caption" color="text">
                                            {detail.customer}
                                        </MDTypography>
                                    )}
                                    {detail.shipmentNo && (
                                        <MDTypography variant="caption" color="info" fontWeight="medium">
                                            {detail.shipmentNo}
                                        </MDTypography>
                                    )}
                                </MDBox>
                            </MDBox>
                            <MDBox display="flex" alignItems="center">
                                <Tooltip title="Refresh from carrier">
                                    <span>
                                        <IconButton onClick={() => onRefresh(detail.id)} disabled={refreshing}>
                                            {refreshing ? <CircularProgress size={18} /> : <SyncIcon fontSize="small" />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={detail.active ? "Pause tracking" : "Resume tracking"}>
                                    <IconButton onClick={() => onToggleActive(detail)}>
                                        {detail.active ? <PauseCircleOutlineIcon fontSize="small" /> : <PlayCircleOutlineIcon fontSize="small" />}
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete tracking">
                                    <IconButton onClick={() => onDelete(detail)} sx={{ color: "#dc2626" }}>
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <IconButton onClick={onClose}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </MDBox>
                        </MDBox>

                        <MDBox
                            display="grid"
                            gap={2}
                            mt={2}
                            sx={{ gridTemplateColumns: { xs: "repeat(2, minmax(0,1fr))", md: "repeat(4, minmax(0,1fr))" } }}
                        >
                            <Field label="Vessel / Voyage" value={[detail.vesselName, detail.voyageNumber].filter(Boolean).join(" / ")} />
                            <Field label={detail.atd ? "ATD" : "ETD"} value={formatDate(detail.atd || detail.etd, true)} />
                            <Field label={detail.ata ? "ATA" : "ETA"} value={formatDate(detail.ata || detail.eta, true)} />
                            <MDBox>
                                <Field label="Arrival" value={hint ? <Pill tone={hint.tone}>{hint.text}</Pill> : "-"} />
                            </MDBox>
                        </MDBox>
                        <MDBox mt={2}>
                            <JourneyBar tracking={detail} />
                        </MDBox>
                        {detail.exceptionReason && (
                            <MDBox mt={1.5} px={1.5} py={1} sx={{ borderRadius: "8px", background: TONE.red.bg, border: `1px solid ${TONE.red.border}` }}>
                                <MDTypography variant="caption" sx={{ color: TONE.red.color, fontWeight: 600 }}>
                                    {detail.exceptionReason}
                                </MDTypography>
                            </MDBox>
                        )}
                    </MDBox>

                    <Box sx={{ px: 3, borderBottom: "1px solid #e5e7eb" }}>
                        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ minHeight: 44, background: "transparent" }}>
                            <Tab value="overview" label="Overview" />
                            <Tab value="events" label={`Events (${detail.events?.length || 0})`} />
                            <Tab value="shipment" label="Shipment" />
                        </Tabs>
                    </Box>

                    <MDBox px={3} py={2.5} sx={{ overflowY: "auto", flex: 1 }}>
                        {tab === "overview" && (
                            <>
                                <OceanShipmentMap trackingId={detail.id} refreshKey={detail.lastApiUpdate} />
                                <Divider sx={{ my: 2.5 }} />
                                <MDTypography variant="button" fontWeight="bold" display="block" mb={1.5}>
                                    Milestones
                                </MDTypography>
                                <Timeline events={detail.events} />
                            </>
                        )}
                        {tab === "events" && <EventsTable events={detail.events} />}
                        {tab === "shipment" && (
                            <MDBox display="grid" gridTemplateColumns="repeat(2, minmax(0,1fr))" gap={2.5}>
                                <Field label="Customer" value={detail.customer} />
                                <Field label="Shipment No" value={detail.shipmentNo} />
                                <Field label="Container" value={[detail.containerNumber, detail.containerType].filter(Boolean).join(" · ")} />
                                <Field label="Carrier / SCAC" value={`${detail.carrierName} · ${detail.scac}`} />
                                <Field label="Booking No" value={detail.bookingNumber} />
                                <Field label="MBL No" value={detail.mblNumber} />
                                <Field label="POL" value={[portText(detail.pol), detail.pol?.name].filter(Boolean).join(" – ")} />
                                <Field label="POD" value={[portText(detail.pod), detail.pod?.name].filter(Boolean).join(" – ")} />
                                <Field label="Transshipment" value={(detail.transshipmentPorts || []).map(portText).join(", ")} />
                                <Field label="IMO" value={detail.imoNumber} />
                                <Field label="Data source" value={detail.lastApiUpdate ? detail.dataSource : "-"} />
                                <Field
                                    label="Last carrier update"
                                    value={`${timeAgo(detail.lastApiUpdate)}${detail.active ? ` · next ${formatDate(detail.nextPollAt, true)}` : " · paused"}`}
                                />
                                {detail.lastError && <Field label="Last error" value={detail.lastError} />}
                                <MDBox gridColumn="1 / -1">
                                    {detail.linkedShipmentId ? (
                                        <MDBox
                                            component="button"
                                            onClick={() => navigate(`/shipping-order/create-ocean-shipment/details?id=${detail.linkedShipmentId}`)}
                                            sx={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 0.75,
                                                border: "1px solid #bfdbfe",
                                                background: "#eff6ff",
                                                color: "#1d4ed8",
                                                borderRadius: "8px",
                                                px: 1.5,
                                                py: 0.75,
                                                fontWeight: 600,
                                                fontSize: 13,
                                                cursor: "pointer",
                                            }}
                                        >
                                            Open Ocean Shipment {detail.shipmentNo} <OpenInNewIcon sx={{ fontSize: "16px !important" }} />
                                        </MDBox>
                                    ) : (
                                        <MDTypography variant="caption" color="text">
                                            Not linked to an Ocean Shipment yet. It links automatically when a shipment contains this container.
                                        </MDTypography>
                                    )}
                                </MDBox>
                            </MDBox>
                        )}
                    </MDBox>
                </MDBox>
            )}
        </Drawer>
    );
}

export default TrackingDrawer;
